/**
 * Builds one frame of the vector overlay + sensor mask from the store at the estimated
 * server tick: radar rings, ghosts, units, missiles (via shared missilePos), intercepts,
 * detonations, ash scars, launch-hint wedges and the input overlays.
 */
import {
  BALANCE,
  TICK_HZ,
  bezierPoint,
  getGeo,
  landHexAt,
  landInRadius,
  missileControlPoint,
  missileProgress,
  missilePos,
  samRange,
  type EntityId,
  type EntityView,
  type InterceptEvent,
  type MissileTrack,
  type Vec2,
} from '@sa/sim';
import { COLORS, type RGB } from '../theme';
import { MotionSmoother, headingOf, isMobileKind } from '../net/interp';
import type { GameStore } from '../state/store';
import { IMPACT_PIP_TICKS, SCAR_TICKS } from '../state/store';
import { SAM_FOCUS_FLASH_MS } from '../state/store';
import { drawGlyph } from './glyphs';
import type { Palette } from './palette';
import type { VectorBatch } from './vector';
import type { VisLayer } from './vis';

export interface Overlay {
  selected: readonly EntityId[];
  /** when the selection last gained a unit or was flashed (performance.now ms): the marker pulses in */
  selectedAt: number;
  /** placement ghost (a ship's floats on its port's water; `port` is the land hex it is built from) */
  placement: { kind: EntityView['kind']; x: number; y: number; valid: boolean; range: number; port?: Vec2 } | null;
  /** placing a sub / carrier: the water spots of all our ports */
  ports: readonly Vec2[] | null;
  /** the last ship move order's marker, `t` 0..1 through its animation (`queue`: a waypoint added) */
  ping: { x: number; y: number; t: number; queue: boolean } | null;
  /** launch / move / strike aim from the selected units to the cursor */
  aim: { kind: 'icbm' | 'slbm' | 'move' | 'air'; from: readonly Vec2[]; x: number; y: number; valid: boolean } | null;
}

/** Where things ended up this frame (for labels and picking). */
export interface SceneOut {
  /** rendered positions of visible entities */
  pos: Map<EntityId, Vec2>;
  /** heads of hostile (not own / allied) missiles in flight, for SAM-priority picking */
  heads: Map<EntityId, Vec2>;
  /** recent launch hints for labels */
  hints: { x: number; y: number; age: number }[];
  /** CRT whiteout from nearby detonations, 0..1 */
  flash: number;
}

const HINT_TTL_S = 20;
/** a launch hint's bearing wedge reaches this far (mu): a hint of direction, not a searchlight */
const HINT_WEDGE_MU = 15;
const TRAIL_STEPS = 28;
const DET_FLASH_TICKS = 12;
const SHOCK_TICKS = 12;
const EMBER_TICKS = 55;
/** the selection marker: corner brackets this far out (css px, ~1.6× a glyph), pulsing in over SELECT_PULSE_MS */
const SELECT_PX = 19;
/** a fighter's air-to-air shot (instant in the sim) is drawn flying this long (ticks) */
const AA_SHOT_TICKS = 5;
const SELECT_PULSE_MS = 600;

/** Outer radius (mu) of the ring-2 blast footprint for hexes of radius `hexR`. */
export const blastRadius = (hexR: number): number => 2.5 * Math.sqrt(3) * hexR;

/** an aircraft this close (mu) to an airbase / carrier of its owner sits on it (a rival's: we have no phase) */
const DECK_MU = 0.35;

interface Deck {
  /** aircraft on deck, per kind: ready ones first, then those building / rearming */
  fighter: { ready: number; busy: number };
  bomber: { ready: number; busy: number };
}

/**
 * Aircraft sitting on their airbase / carrier are not drawn over it (their glyph would sit on the
 * base's): the base wears one small pip per aircraft instead. Returns the hidden aircraft and,
 * per base, what sits on it.
 */
export function aircraftOnDeck(entities: Iterable<EntityView>, me: number, pos: (e: EntityView) => Vec2): { hidden: Set<EntityId>; decks: Map<EntityId, Deck> } {
  const hidden = new Set<EntityId>();
  const decks = new Map<EntityId, Deck>();
  const bases: EntityView[] = [];
  const air: EntityView[] = [];
  for (const e of entities) {
    if (e.kind === 'airbase' || e.kind === 'carrier') bases.push(e);
    else if (e.kind === 'fighter' || e.kind === 'bomber') air.push(e);
  }
  const deckOf = (id: EntityId): Deck => {
    let d = decks.get(id);
    if (!d) decks.set(id, (d = { fighter: { ready: 0, busy: 0 }, bomber: { ready: 0, busy: 0 } }));
    return d;
  };
  for (const a of air) {
    const kind = a.kind as 'fighter' | 'bomber';
    let base: EntityView | undefined;
    let ready = true;
    const ph = a.detail?.phase;
    if (a.owner === me && ph !== undefined) {
      if (ph !== 'parked' && ph !== 'building' && ph !== 'rearming') continue;
      base = bases.find((b) => b.id === a.detail!.baseId);
      ready = ph === 'parked' && (kind !== 'bomber' || (a.detail!.ammo ?? 1) > 0);
    } else {
      const p = pos(a);
      base = bases.find((b) => {
        if (b.owner !== a.owner) return false;
        const q = pos(b);
        return (q.x - p.x) ** 2 + (q.y - p.y) ** 2 <= DECK_MU * DECK_MU;
      });
    }
    if (!base) continue;
    hidden.add(a.id);
    deckOf(base.id)[kind][ready ? 'ready' : 'busy']++;
  }
  return { hidden, decks };
}

const sensorRange = (e: EntityView): number => {
  const v = BALANCE.vision;
  switch (e.kind) {
    case 'radar':
      return v.radar;
    case 'carrier':
      return v.carrierRadar;
    case 'fighter':
      return v.fighter;
    case 'bomber':
      return v.bomber;
    case 'sam':
      return v.sam;
    case 'city':
      return v.city;
    default:
      return 0;
  }
};

export class SceneBuilder {
  private smoother = new MotionSmoother();
  private tmp: Vec2 = { x: 0, y: 0 };
  /** entities last seen under construction, and completion times (ms) for the "+" tick */
  private wasBuilding = new Set<EntityId>();
  private hexR = 1;
  private done = new Map<EntityId, number>();
  /** our team's sensor circles this frame (an exposed rival SAM inside one is worth its ring) */
  private sensors: { x: number; y: number; r: number }[] = [];
  /** exposed rival SAMs near our team's land (cached a second: a disk of hexes per SAM) */
  private nearUs = new Map<EntityId, { near: boolean; untilMs: number }>();

  constructor(
    private vb: VectorBatch,
    private vis: VisLayer,
  ) {}

  /**
   * An exposed rival SAM worth its loud rings: inside our team's sensors, or within 1.5× SAM
   * range of our team's land (it guards what we would strike).
   */
  private samMatters(e: EntityView, store: GameStore, pal: Palette, nowMs: number): boolean {
    if (this.sensors.some((c) => (c.x - e.x) ** 2 + (c.y - e.y) ** 2 <= c.r * c.r)) return true;
    const hit = this.nearUs.get(e.id);
    if (hit && nowMs < hit.untilMs) return hit.near;
    const v = store.view;
    const near = landInRadius(getGeo(store.world), store.world, e.x, e.y, 1.5 * samRange(false)).some((h) => v.owner[h] > 0 && pal.isFriendly(v.owner[h]));
    if (this.nearUs.size > 128) this.nearUs.clear();
    this.nearUs.set(e.id, { near, untilMs: nowMs + 1000 });
    return near;
  }

  build(
    store: GameStore,
    pal: Palette,
    tick: number,
    nowMs: number,
    time: number,
    ov: Overlay,
    zoomCss: number,
    view: [number, number, number, number],
    /** is this world point under a HUD block (credits, share bar)? Map glyphs there are not drawn */
    underHud: (x: number, y: number) => boolean = () => false,
  ): SceneOut {
    const vb = this.vb;
    const v = store.view;
    vb.begin(zoomCss, view);
    this.vis.clear();
    this.sensors.length = 0;
    const out: SceneOut = { pos: new Map(), heads: new Map(), hints: [], flash: 0 };
    const mobileTick = tick - 1;

    // ---- positions + sensors
    for (const e of v.entities.values()) {
      const p = isMobileKind(e.kind) || e.kind === 'bomb' ? this.smoother.pos(e, mobileTick, nowMs, { x: 0, y: 0 }) : { x: e.x, y: e.y };
      out.pos.set(e.id, p);
      if (pal.isFriendly(e.owner) && e.state !== 'building') {
        const r = sensorRange(e);
        // only our radars and carriers draw the coverage outline (one smooth line, no lobes
        // for every city and SAM); everything else — and every allied sensor — just lights the fog
        if (r > 0) {
          this.vis.add(p.x, p.y, r, r >= BALANCE.vision.fighter, e.owner === v.me && (e.kind === 'radar' || e.kind === 'carrier'));
          this.sensors.push({ x: p.x, y: p.y, r });
        }
      }
    }
    this.smoother.retain((id) => v.entities.has(id));
    for (const id of this.wasBuilding) if (!v.entities.has(id)) this.wasBuilding.delete(id);

    // ---- radars: the union outline of coverage is drawn by the territory pass; here only
    // a faint sweep per radar, the future ring of one under construction, and the ring of
    // a selected radar
    const radars: { x: number; y: number; r: number }[] = [];
    for (const e of v.entities.values()) {
      if ((e.kind !== 'radar' && e.kind !== 'carrier') || e.owner !== v.me) continue;
      const p = out.pos.get(e.id)!;
      const r = e.kind === 'radar' ? BALANCE.vision.radar : BALANCE.vision.carrierRadar;
      const col = COLORS.me;
      if (e.state === 'building') {
        vb.ring(p.x, p.y, r, col, 0.2, { width: 1, dash: [3, 6] });
        continue;
      }
      radars.push({ x: p.x, y: p.y, r });
      const phase = (e.id * 1.7) % (Math.PI * 2);
      vb.sweep(p.x, p.y, r, time * 0.7 + phase, 0.5, col, 0.03);
    }
    // range ticks every 30° on the coverage edge (only where no other radar covers them)
    for (const rd of radars) {
      for (let a = 0; a < 360; a += 30) {
        const t = (a * Math.PI) / 180;
        const c = Math.cos(t);
        const sn = Math.sin(t);
        const tx = rd.x + c * rd.r;
        const ty = rd.y + sn * rd.r;
        if (radars.some((o) => o !== rd && (o.x - tx) ** 2 + (o.y - ty) ** 2 < (o.r - 0.3) ** 2)) continue;
        const l = a % 90 === 0 ? 6 : 3;
        vb.line(tx, ty, tx, ty, COLORS.me, 0.35, { width: 1 }, 0, 0, -c * l, -sn * l);
      }
    }

    // ---- detonations: white core + CRT lift, an expanding shock ring out to the ring-2
    // footprint, a few seconds of ember glow, then a persistent ash octagon
    this.hexR = store.world.hexR;
    const blastR = blastRadius(this.hexR);
    // (the world behind the results is frozen: no glare stuck mid-flash, only the ash)
    const over = !!v.result;
    for (const d of store.scars) {
      const age = over ? Math.max(tick - d.tick, EMBER_TICKS) : tick - d.tick;
      if (age < 0) continue;
      const big = d.exact ? 1 : 0.45;
      if (age < DET_FLASH_TICKS) {
        const k = 1 - age / DET_FLASH_TICKS;
        const col = d.exact ? COLORS.white : COLORS.amber;
        vb.glowDot(d.x, d.y, 70 * big * (0.35 + 0.65 * k), col, 0.95 * k * k);
        vb.disc(d.x, d.y, 3 * big * k + 0.5, COLORS.white, k);
        if (d.exact) out.flash = Math.max(out.flash, k * k);
      }
      if (age < SHOCK_TICKS) {
        const u = age / SHOCK_TICKS;
        const r = blastR * big * (1 - (1 - u) ** 3);
        vb.ring(d.x, d.y, Math.max(0.05, r), COLORS.white, 0.85 * (1 - u), { width: 1.5, glow: 5 });
      }
      if (age < EMBER_TICKS) {
        const u = age / EMBER_TICKS;
        const flicker = 0.85 + 0.15 * Math.sin(time * 9 + d.x);
        vb.glowDot(d.x, d.y, 26 * big, COLORS.amber, 0.5 * (1 - u) * flicker);
      }
      if (!d.exact) continue;
      const fade = 0.35 + 0.65 * Math.max(0, 1 - age / SCAR_TICKS);
      // the scorched footprint fades as the radiation there decays
      const h = landHexAt(getGeo(store.world), d.x, d.y);
      const rad = h >= 0 ? v.rad[h] / 15 : 0;
      this.ashMark(d.x, d.y, blastR, Math.min(1, age / 30) * fade, rad);
    }

    // ---- our own impacts: an amber flash pip where each warhead landed, seen or not (a strike
    // into the fog still ends with something on the map), fading over 10 s
    if (!over) {
      for (const p of store.impacts) {
        const age = tick - p.tick;
        if (age < 0 || age > IMPACT_PIP_TICKS) continue;
        const k = 1 - age / IMPACT_PIP_TICKS;
        const blink = age < 20 ? 0.6 + 0.4 * Math.sin(time * 18) : 1;
        vb.glowDot(p.x, p.y, 28, COLORS.amber, 0.85 * k * blink);
        vb.disc(p.x, p.y, 2.6, COLORS.white, k);
        vb.ring(p.x, p.y, 11, COLORS.amber, 0.9 * k, { px: true, width: 1.5 });
      }
    }

    // ---- launch hints: satellite flash + uncertain bearing wedge, no track (a spectator sees
    // everything: nothing hints at fog for them)
    for (const h of v.eliminated ? [] : store.hints) {
      const age = (tick - h.tick) / TICK_HZ;
      if (age < 0 || age > HINT_TTL_S) continue;
      const k = 1 - age / HINT_TTL_S;
      const col = COLORS.amber;
      const pulse = 0.75 + 0.25 * Math.sin(time * 5);
      vb.glowDot(h.x, h.y, 14, col, 0.55 * k * pulse);
      vb.disc(h.x, h.y, 1.6, COLORS.white, 0.9 * k);
      vb.ring(h.x, h.y, 12, col, 0.45 * k, { px: true, width: 1 });
      vb.ring(h.x, h.y, 24, col, 0.22 * k, { px: true, width: 1, dash: [2, 4] });
      vb.ring(h.x, h.y, 40, col, 0.1 * k, { px: true, width: 1, dash: [1, 5] });
      const L = HINT_WEDGE_MU;
      vb.wedge(h.x, h.y, 0.6, L, h.bearing - h.spread, h.bearing + h.spread, col, 0.09 * k);
      for (const a of [h.bearing - h.spread, h.bearing + h.spread]) {
        vb.line(h.x, h.y, h.x + Math.cos(a) * L * 0.7, h.y + Math.sin(a) * L * 0.7, col, 0.22 * k, { width: 1, dash: [2, 7], fadeB: 0 });
      }
      out.hints.push({ x: h.x, y: h.y, age });
    }

    // ---- ghosts: last known, dim, fading with age
    for (const g of v.ghosts.values()) {
      if (underHud(g.x, g.y)) continue;
      const age = (tick - g.tick) / TICK_HZ;
      const col = pal.color(g.owner);
      const a = 0.42 * Math.max(0.35, 1 - age / 120);
      drawGlyph(vb, g.kind, g.x, g.y, col, a, { heading: g.heading ?? null, level: g.extra, dashed: true });
      if (g.heading !== undefined) {
        const c = Math.cos(g.heading);
        const s = Math.sin(g.heading);
        vb.glyph(g.x, g.y, c * 8, s * 8, c * 26, s * 26, col, a * 0.7, { width: 1, dash: [1, 4] });
      }
      if (g.id < 0) vb.ring(g.x, g.y, 10, col, a, { px: true, width: 1, dash: [2, 3] });
    }

    // ---- units
    const geo = getGeo(store.world);
    const capHex = store.capitalHex;
    const capitalThreat = store.inboundThreats.some((t) => t.stake === 'CAPITAL' || t.critical);
    const critical = v.criticalCapitals.has(v.me);
    const deck = aircraftOnDeck(v.entities.values(), v.me, (e) => out.pos.get(e.id) ?? e);
    for (const e of v.entities.values()) {
      if (e.kind === 'icbm' || e.kind === 'slbm' || e.kind === 'interceptor') continue;
      if (deck.hidden.has(e.id)) continue;
      const p = out.pos.get(e.id)!;
      // (under the top HUD band: the share bar and credits stay readable)
      if (underHud(p.x, p.y)) continue;
      const friendly = pal.isFriendly(e.owner);
      const mine = e.owner === v.me;
      // an ally's base is context, not ours: quiet, no glow; its silos (a launch source) a little louder
      const ally = friendly && !mine;
      let col: RGB = pal.color(e.owner);
      let a = mine ? 0.9 : ally ? (e.kind === 'silo' ? 0.5 : 0.3) : e.owner === 0 ? 0.4 : 0.85;
      const isCapital = e.kind === 'city' && mine && landHexAt(geo, e.x, e.y) === capHex;
      if (isCapital) col = COLORS.white;
      else if (e.kind === 'city' && mine) a = 0.65;
      const building = e.state === 'building';
      if (building) a *= 0.8;
      // our SAM on emissions control: dimmer (its radar is off)
      const silent = mine && e.kind === 'sam' && !!e.detail?.silent;
      if (silent) a *= 0.55;
      drawGlyph(vb, e.kind, p.x, p.y, col, a, {
        heading: headingOf(e),
        level: e.kind === 'city' ? e.extra : undefined,
        dashed: building,
        glow: e.kind === 'city' && !ally ? 1 : 0,
      });
      if ((e.kind === 'sub' || e.kind === 'carrier') && (e.vx !== 0 || e.vy !== 0)) this.wake(e, p, col, a, time);
      if (building) this.constructionSite(e, p, col, tick, mine);
      else if (this.wasBuilding.delete(e.id) && mine) this.done.set(e.id, nowMs);
      const doneAt = this.done.get(e.id);
      if (doneAt !== undefined) {
        const u = (nowMs - doneAt) / 1500;
        if (u >= 1) this.done.delete(e.id);
        else {
          // completion: a "+" tick and one ring
          vb.glyph(p.x, p.y, 10, -12, 16, -12, col, 1 - u, { width: 1.5 });
          vb.glyph(p.x, p.y, 13, -15, 13, -9, col, 1 - u, { width: 1.5 });
          vb.ring(p.x, p.y, 10 + 10 * u, col, 0.6 * (1 - u), { px: true, width: 1.2 });
        }
      }
      if (e.state === 'exposed' && e.owner === v.me) {
        const pulse = 0.5 + 0.5 * Math.sin(time * 6);
        vb.ring(p.x, p.y, 10, COLORS.amber, 0.35 + 0.4 * pulse, { px: true, width: 1, dash: [2, 2] });
      } else if (e.state === 'exposed' && e.kind === 'sam' && !friendly && !this.samMatters(e, store, pal, nowMs)) {
        // exposed far from us, out of our sensors: a tiny dot, no more (someone else's fight)
        vb.disc(p.x, p.y, 2, COLORS.amber, 0.55);
      } else if (e.state === 'exposed' && e.kind === 'sam' && !friendly) {
        // a rival SAM that fired in our sensors or near our land: its radar gave it away — a bright
        // ring, and rings radiating from it while everyone can see it (a target). Zoomed out it
        // shrinks to a quiet ring: full from home zoom (12 css px / mu), no glow or ripples below 6
        const k = Math.min(1, Math.max(0, (zoomCss - 4) / 8));
        vb.ring(p.x, p.y, 7 + 4 * k, COLORS.amber, 0.45 + 0.35 * k, { px: true, width: 1 + 0.4 * k, glow: k > 0.7 ? 3 : 0 });
        if (k > 0.25)
          for (let j = 0; j < 2; j++) {
            const u = (time * 0.8 + j * 0.5) % 1;
            vb.ring(p.x, p.y, 8 + 4 * k + 22 * k * u, COLORS.amber, 0.7 * k * (1 - u), { px: true, width: 1.2 });
          }
      }
      if (e.kind === 'sam' && mine) {
        // SILENT: a dashed grey ring (its radar is off)
        if (silent) vb.ring(p.x, p.y, 12, COLORS.neutral, 0.9, { px: true, width: 1.3, dash: [3, 3] });
      }
      const onDeck = deck.decks.get(e.id);
      if (onDeck) this.deckPips(onDeck, p, col, a);
      if ((e.kind === 'sam' || e.kind === 'carrier') && mine && !building) this.ammoPips(e, p, col, a, tick);
      if (e.kind === 'silo' && e.owner === v.me) {
        // one pip per loaded ICBM
        const n = e.detail?.loaded ?? 0;
        for (let i = 0; i < n; i++) vb.glyph(p.x, p.y, -4.5 + i * 3, 8, -3.2 + i * 3, 8, col, 0.9, { width: 1.6 });
      }
      if (e.kind === 'city' && e.state === 'braced') this.braceRing(e, p, pal.color(e.owner), tick);
      // our capital from 10:00: it can fall now — a green ◇ in a thin shield ring that turns
      // red while a warhead is on its way to it (rival capitals are amber ◇ with ticks); while
      // critical (§1.11) the whole mark pulses red and pings outward
      if (isCapital && v.capitalsExposed && critical) this.criticalMark(p.x, p.y, COLORS.danger, time, capitalThreat ? 9 : 4.5, 1);
      else if (isCapital && v.capitalsExposed) {
        this.diamond(p.x, p.y, 13, COLORS.me, 0.9, 1.4);
        if (capitalThreat) vb.ring(p.x, p.y, 19, COLORS.danger, 0.55 + 0.4 * Math.sin(time * 8), { px: true, width: 1.4 });
        else vb.ring(p.x, p.y, 19, COLORS.me, 0.35, { px: true, width: 1 });
      }
    }
    if (v.capitalsExposed) this.capitalTargets(store, pal, underHud, time);

    // ---- SAM priorities: a thin line from the SAM to the missile it engages first; right after
    // T the SAMs just told to engage flash bright lines to it (before the server's answer)
    const focus = store.samFocus && nowMs - store.samFocus.atMs < SAM_FOCUS_FLASH_MS ? store.samFocus : null;
    const flash = focus ? 1 - (nowMs - focus.atMs) / SAM_FOCUS_FLASH_MS : 0;
    for (const e of v.entities.values()) {
      if (e.kind !== 'sam' || e.owner !== v.me) continue;
      const flashing = focus?.sams.includes(e.id) ?? false;
      const id = flashing ? focus!.threatId : e.detail?.priorityId;
      const t = id ? v.tracks.get(id) : undefined;
      if (!t || tick > t.launchTick + t.flightTicks) continue;
      const p = out.pos.get(e.id)!;
      const head = missilePos(t, Math.max(t.launchTick, tick), { x: 0, y: 0 });
      if (flashing) vb.line(p.x, p.y, head.x, head.y, COLORS.white, 0.6 + 0.4 * flash, { width: 1.2 + 1.2 * flash, dash: [5, 3], glow: 4 * flash });
      else vb.line(p.x, p.y, head.x, head.y, COLORS.me, 0.75, { width: 1.2, dash: [5, 3] });
    }
    // the engaged missile heads wear brackets (the focused one bigger while it flashes)
    const engaged = new Set<EntityId>();
    for (const e of v.entities.values()) if (e.kind === 'sam' && e.owner === v.me && e.detail?.priorityId) engaged.add(e.detail.priorityId);
    if (focus) engaged.add(focus.threatId);
    for (const id of engaged) {
      const t = v.tracks.get(id);
      if (!t || tick > t.launchTick + t.flightTicks) continue;
      const head = missilePos(t, Math.max(t.launchTick, tick), { x: 0, y: 0 });
      const big = focus?.threatId === id ? flash : 0;
      this.brackets(head.x, head.y, 9 + 5 * big, big > 0 ? COLORS.white : COLORS.me, 0.9, 1.2 + big);
    }

    // ---- selection: one marker for every kind (corner brackets ~1.6× a glyph, the same at every
    // zoom) that closes in on the unit with a ring when it is selected, then breathes; + its range ring
    const breathe = 0.8 + 0.2 * Math.sin(time * Math.PI * 2);
    const u = Math.min(1, Math.max(0, (nowMs - ov.selectedAt) / SELECT_PULSE_MS));
    const ease = 1 - (1 - u) ** 3;
    for (const id of ov.selected) {
      const p = out.pos.get(id);
      const e = v.entities.get(id);
      if (!p || !e) continue;
      const s = SELECT_PX + 16 * (1 - ease);
      this.brackets(p.x, p.y, s, COLORS.white, u < 1 ? 1 : 0.95 * breathe, 2, 7, 3);
      if (u < 1) vb.ring(p.x, p.y, SELECT_PX + 34 * (1 - ease), COLORS.white, 0.7 * (1 - u), { px: true, width: 1.5, glow: 3 });
      const r =
        e.kind === 'sam'
          ? samRange(!!e.detail?.silent)
          : e.kind === 'sub'
            ? BALANCE.sub.slbmRange
            : e.kind === 'radar'
              ? BALANCE.vision.radar
              : e.kind === 'carrier'
                ? BALANCE.vision.carrierRadar
                : 0;
      if (r) vb.ring(p.x, p.y, r, COLORS.me, 0.35, { width: 1, dash: [3, 5] });
      if ((e.kind === 'sub' || e.kind === 'carrier') && e.owner === v.me) this.shipRoute(e, p, zoomCss, breathe);
      if ((e.kind === 'bomber' || e.kind === 'fighter') && e.detail?.tx !== undefined && e.detail.phase !== 'parked') {
        vb.line(p.x, p.y, e.detail.tx, e.detail.ty!, COLORS.me, 0.35, { width: 1, dash: [2, 5] });
      }
    }

    // ---- missiles
    for (const t of v.tracks.values()) this.missile(store, pal, t, tick, out);

    // ---- intercepts: interceptor climbs to meet the missile; burst on resolve
    for (const i of v.intercepts) {
      const shooter = out.pos.get(i.shooterId) ?? v.ghosts.get(i.shooterId) ?? null;
      const track = v.tracks.get(i.targetId) ?? store.goneTracks.get(i.targetId)?.track ?? null;
      if (!track) {
        // not a missile: a shot at an aircraft
        this.airShot(store, out, i, tick);
        continue;
      }
      const meet = missilePos(track, i.resolveTick, { x: 0, y: 0 });
      if (tick >= i.fireTick && tick <= i.resolveTick && shooter) {
        const k = (tick - i.fireTick) / Math.max(1, i.resolveTick - i.fireTick);
        this.interceptor(shooter, meet, k);
      }
      if (i.hit !== undefined && tick >= i.resolveTick) {
        const age = (tick - i.resolveTick) / TICK_HZ;
        if (age > 2.5) continue;
        const k = 1 - age / 2.5;
        if (i.hit) {
          vb.glowDot(meet.x, meet.y, 26, COLORS.white, 0.9 * k);
          vb.ring(meet.x, meet.y, 6 + 26 * (1 - k), COLORS.white, 0.8 * k, { px: true, width: 1.5, glow: 4 });
          const s = 6;
          vb.glyph(meet.x, meet.y, 0, -s, s, 0, COLORS.white, k);
          vb.glyph(meet.x, meet.y, s, 0, 0, s, COLORS.white, k);
          vb.glyph(meet.x, meet.y, 0, s, -s, 0, COLORS.white, k);
          vb.glyph(meet.x, meet.y, -s, 0, 0, -s, COLORS.white, k);
        } else {
          // a miss: a small × where the interceptor passed
          const s = 5;
          vb.glyph(meet.x, meet.y, -s, -s, s, s, COLORS.neutral, 0.8 * k, { width: 1.4 });
          vb.glyph(meet.x, meet.y, -s, s, s, -s, COLORS.neutral, 0.8 * k, { width: 1.4 });
        }
      }
    }

    // ---- SAMs that can engage the most urgent threat: their reach pulses
    const urgent = store.inboundThreats?.[0];
    if (urgent) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 5);
      for (const id of urgent.coveringSams) {
        const p = out.pos.get(id);
        const s = v.entities.get(id);
        const r = s?.kind === 'carrier' ? BALANCE.carrier.samRange : samRange(!!s?.detail?.silent);
        if (p) vb.ring(p.x, p.y, r, COLORS.me, 0.25 + 0.3 * pulse, { width: 1.2, dash: [4, 4] });
      }
    }

    // ---- input overlays (an invalid site is dim grey with a ⊘ — red is for danger only)
    if (ov.ports) this.ports(ov.ports, time);
    if (ov.ping) this.orderPing(ov.ping);
    const pl = store.placement ?? ov.placement;
    if (ov.placement?.port) vb.line(ov.placement.port.x, ov.placement.port.y, ov.placement.x, ov.placement.y, ov.placement.valid ? COLORS.me : COLORS.neutral, 0.6, { width: 1, dash: [2, 3] });
    if (pl) {
      const col = pl.valid ? COLORS.me : COLORS.neutral;
      drawGlyph(vb, pl.kind, pl.x, pl.y, col, pl.valid ? 0.9 : 0.5, { level: 1, glow: pl.valid ? 3 : 0 });
      if (pl.range > 0) vb.ring(pl.x, pl.y, pl.range, col, pl.valid ? 0.45 : 0.2, { width: 1, dash: [4, 4] });
      if (pl.valid) this.brackets(pl.x, pl.y, 12, col, 0.6);
      else {
        vb.ring(pl.x, pl.y, 13, col, 0.75, { px: true, width: 1.3 });
        vb.glyph(pl.x, pl.y, -9, 9, 9, -9, col, 0.75, { width: 1.3 });
      }
    }
    if (ov.aim) this.aim(ov.aim, store.aim, time);
    return out;
  }

  /**
   * Our ship's water route (owner detail): a dotted line from where it is drawn through the
   * remaining waypoints (dots) to its destination (a small ◇) — the order it is carrying out, at a glance.
   */
  private shipRoute(e: EntityView, p: Vec2, zoomCss: number, breathe: number): void {
    const vb = this.vb;
    const d = e.detail;
    const route = d?.route ?? [];
    if (!route.length || d?.destX === undefined || d.destY === undefined) return;
    let ax = p.x;
    let ay = p.y;
    let along = 0;
    for (let i = 0; i < route.length; i += 2) {
      const bx = route[i];
      const by = route[i + 1];
      vb.line(ax, ay, bx, by, COLORS.me, 0.55, { width: 1.2, dash: [2, 5], phase: along });
      along += Math.hypot(bx - ax, by - ay) * zoomCss;
      // a queued waypoint: a dot
      if (i + 2 < route.length) vb.disc(bx, by, 2.2, COLORS.me, 0.7);
      ax = bx;
      ay = by;
    }
    // the destination: a small open diamond (⊗ is ash only; a ring would read as a radar)
    this.diamond(d.destX, d.destY, 6, COLORS.me, 0.85 * breathe, 1.4);
  }

  /** A moving ship trails a short wake (two lines astern), so motion reads even at low zoom. */
  private wake(e: EntityView, p: Vec2, col: RGB, a: number, time: number): void {
    const h = Math.atan2(e.vy, e.vx);
    const c = Math.cos(h);
    const s = Math.sin(h);
    const flick = 0.75 + 0.25 * Math.sin(time * 6 + e.id);
    for (const side of [-1, 1]) {
      const ox = -c * 9 - s * side * 2.5;
      const oy = -s * 9 + c * side * 2.5;
      this.vb.glyph(p.x, p.y, ox, oy, ox - c * 10 - s * side * 3, oy - s * 10 + c * side * 3, col, a * 0.5 * flick, { width: 1, fadeB: 0 });
    }
  }

  /**
   * Placing a sub / carrier: each of our ports (where the ship would be launched) gets a small
   * dim anchor on its water — not a ring, which is the radar's glyph.
   */
  private ports(ports: readonly Vec2[], time: number): void {
    const a = 0.5 + 0.15 * Math.sin(time * 3);
    const segs: [number, number, number, number][] = [
      // stem, stock, and the crown's curve
      [0, -5, 0, 4],
      [-2.5, -2.5, 2.5, -2.5],
      [-4.5, 1, -3, 3.5],
      [-3, 3.5, 0, 4.5],
      [0, 4.5, 3, 3.5],
      [3, 3.5, 4.5, 1],
    ];
    for (const q of ports) for (const [x0, y0, x1, y1] of segs) this.vb.glyph(q.x, q.y, x0, y0, x1, y1, COLORS.me, a, { width: 1.1 });
  }

  /** A move order lands: a ring closing in on the spot with four ticks (smaller for a waypoint). */
  private orderPing(pg: NonNullable<Overlay['ping']>): void {
    const k = 1 - pg.t;
    const big = pg.queue ? 0.7 : 1;
    const r = (6 + 16 * k * k) * big;
    this.vb.ring(pg.x, pg.y, r, COLORS.me, 0.9 * k, { px: true, width: 1.4, glow: 3 * k });
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      this.vb.glyph(pg.x, pg.y, dx * (r + 2), dy * (r + 2), dx * (r + 7), dy * (r + 7), COLORS.me, 0.9 * k, { width: 1.3 });
    }
  }

  /** One pip per aircraft on an airbase / carrier, under its glyph: fighters short, bombers long (dim: not ready yet). */
  private deckPips(d: Deck, p: Vec2, col: RGB, a: number): void {
    const vb = this.vb;
    let x = -5;
    for (const kind of ['fighter', 'bomber'] as const) {
      const len = kind === 'fighter' ? 1.4 : 3;
      const k = d[kind];
      for (let i = 0; i < k.ready + k.busy; i++) {
        vb.glyph(p.x, p.y, x, 9, x + len, 9, col, i < k.ready ? a : a * 0.35, { width: 1.6 });
        x += len + 1.8;
      }
    }
  }

  /**
   * Our own SAM's / carrier's interceptors as a row of pips (a SAM's under it, a carrier's above
   * the hull: its deck pips are below); the next one fills while it reloads. Never a rival's.
   */
  private ammoPips(e: EntityView, p: Vec2, col: RGB, a: number, tick: number): void {
    const carrier = e.kind === 'carrier';
    const max = carrier ? BALANCE.carrier.samAmmo : BALANCE.sam.ammo;
    const reloadTicks = carrier ? BALANCE.carrier.samReloadTicks : BALANCE.sam.reloadTicks;
    const n = Math.min(max, e.detail?.ammo ?? max);
    const reloadAt = e.detail?.reloadAt ?? 0;
    const fill = n < max && reloadAt > 0 ? Math.min(1, Math.max(0, 1 - (reloadAt - tick) / reloadTicks)) : 0;
    const y = carrier ? -10 : 9;
    const gap = carrier ? 2.5 : 3;
    const len = carrier ? 1.1 : 1.3;
    const x0 = -((max - 1) * gap + len) / 2;
    for (let i = 0; i < max; i++) {
      const x = x0 + i * gap;
      this.vb.glyph(p.x, p.y, x, y, x + len, y, col, i < n ? a : a * 0.22, { width: 1.6 });
      if (i === n && fill > 0) this.vb.glyph(p.x, p.y, x, y, x + len * fill, y, col, a, { width: 1.6 });
    }
  }

  /**
   * A braced city (§1.15): a shield ring; ours also shows the time left as an arc that
   * shrinks clockwise (the countdown itself is a label).
   */
  private braceRing(e: EntityView, p: Vec2, col: RGB, tick: number): void {
    const vb = this.vb;
    vb.ring(p.x, p.y, 13, col, 0.3, { px: true, width: 1 });
    const until = e.detail?.braceUntil;
    if (until === undefined) {
      vb.ring(p.x, p.y, 13, col, 0.85, { px: true, width: 1.6, glow: 4 });
      return;
    }
    const left = Math.min(1, Math.max(0, (until - tick) / BALANCE.brace.ticks));
    vb.ring(p.x, p.y, 13, col, 0.95, { px: true, width: 1.8, glow: 4, a0: -Math.PI / 2, a1: -Math.PI / 2 + left * Math.PI * 2 + 1e-3 });
  }

  /**
   * A critical capital (§1.11): a ◇ and ring pulsing at `rate` (rad/s) plus a ring pinging
   * outward. `a` scales it all (a rival's is a little quieter than our own).
   */
  private criticalMark(x: number, y: number, col: RGB, time: number, rate: number, a: number): void {
    const k = 0.5 + 0.5 * Math.sin(time * rate);
    this.diamond(x, y, 13, col, a * (0.6 + 0.4 * k), 1.6);
    this.vb.ring(x, y, 19, col, a * (0.45 + 0.5 * k), { px: true, width: 1.6, glow: 4 * k });
    const u = (time * 0.6) % 1;
    this.vb.ring(x, y, 19 + 26 * u, col, a * 0.5 * (1 - u), { px: true, width: 1 });
  }

  /** ◇ outline around (x, y), `r` css px. */
  private diamond(x: number, y: number, r: number, col: RGB, a: number, width = 1.2): void {
    const pts: [number, number][] = [
      [0, -r],
      [r, 0],
      [0, r],
      [-r, 0],
    ];
    for (let i = 0; i < 4; i++) {
      const [ax, ay] = pts[i];
      const [bx, by] = pts[(i + 1) % 4];
      this.vb.glyph(x, y, ax, ay, bx, by, col, a, { width });
    }
  }

  /**
   * From 10:00 every rival capital is a strategic target: its (public) spot gets a ◇ with four
   * ticks in the owner's colour, whether or not we can see the city right now. A critical one
   * (one more hit takes it, §1.11) pulses and pings for its window: a target of opportunity.
   */
  private capitalTargets(store: GameStore, pal: Palette, underHud: (x: number, y: number) => boolean, time: number): void {
    const v = store.view;
    const out = store.eliminated;
    for (const p of v.players) {
      if (pal.isFriendly(p.id) || out.has(p.id)) continue;
      const h = store.capitalOf(p.id);
      if (h < 0) continue;
      const x = store.world.cx[h];
      const y = store.world.cy[h];
      if (underHud(x, y)) continue;
      const col = pal.color(p.id);
      if (v.criticalCapitals.has(p.id)) this.criticalMark(x, y, col, time, 4.5, 0.9);
      else this.diamond(x, y, 12, col, 0.75);
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        this.vb.glyph(x, y, dx * 14, dy * 14, dx * 18, dy * 18, col, 0.75, { width: 1.2 });
      }
    }
  }

  /**
   * R2-A ash scar: ⨯ inside an octagon, the scorched ring-1 footprint as a faint octagon (it
   * fades with the radiation there, `rad` 0..1) and the dashed ring-2 footprint.
   */
  private ashMark(x: number, y: number, blastR: number, a: number, rad: number): void {
    const vb = this.vb;
    const grey = COLORS.neutral;
    const r1 = blastR * 0.6;
    const fa = 0.3 * a * (0.35 + 0.65 * rad);
    for (let i = 0; i < 8; i++) {
      const t0 = ((i + 0.5) * Math.PI) / 4;
      const t1 = ((i + 1.5) * Math.PI) / 4;
      vb.line(x + Math.cos(t0) * r1, y + Math.sin(t0) * r1, x + Math.cos(t1) * r1, y + Math.sin(t1) * r1, grey, fa, { width: 1.4 });
    }
    const s = 9;
    for (let i = 0; i < 8; i++) {
      const t0 = ((i + 0.5) * Math.PI) / 4;
      const t1 = ((i + 1.5) * Math.PI) / 4;
      vb.glyph(x, y, Math.cos(t0) * s, Math.sin(t0) * s, Math.cos(t1) * s, Math.sin(t1) * s, grey, 0.75 * a, { width: 1.3 });
    }
    vb.glyph(x, y, -3.5, -3.5, 3.5, 3.5, grey, 0.8 * a, { width: 1.3 });
    vb.glyph(x, y, -3.5, 3.5, 3.5, -3.5, grey, 0.8 * a, { width: 1.3 });
    vb.ring(x, y, blastR, grey, 0.25 * a, { width: 1, dash: [2, 5] });
  }

  /** Under construction: dashed glyph (drawn by the caller), a progress arc and a pour pulse. */
  private constructionSite(e: EntityView, p: Vec2, col: RGB, tick: number, friendly: boolean): void {
    const vb = this.vb;
    this.wasBuilding.add(e.id);
    const age = (tick - e.segTick) / TICK_HZ;
    if (age >= 0 && age < 1.2) {
      const u = age / 1.2;
      vb.ring(p.x, p.y, 6 + 18 * u, col, 0.7 * (1 - u), { px: true, width: 1.4 });
    }
    if (!friendly) return;
    vb.ring(p.x, p.y, 11, col, 0.25, { px: true, width: 1.6 });
    const done = e.detail?.buildDoneTick;
    if (done === undefined) return;
    const prog = Math.min(1, Math.max(0, (tick - e.segTick) / Math.max(1, done - e.segTick)));
    vb.ring(p.x, p.y, 11, col, 0.9, { px: true, width: 1.6, a0: -Math.PI / 2, a1: -Math.PI / 2 + prog * Math.PI * 2 + 1e-3 });
  }

  /** Four corner brackets `s` css px out from (x, y), arms `l` long. */
  private brackets(x: number, y: number, s: number, col: RGB, a: number, width = 1.2, l = 4, glow = 0): void {
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ]) {
      this.vb.glyph(x, y, sx * s, sy * s, sx * (s - l), sy * s, col, a, { width, glow });
      this.vb.glyph(x, y, sx * s, sy * s, sx * s, sy * (s - l), col, a, { width, glow });
    }
  }

  private missile(store: GameStore, pal: Palette, t: MissileTrack, tick: number, out: SceneOut): void {
    const vb = this.vb;
    const impact = t.launchTick + t.flightTicks;
    if (tick > impact + 3) return;
    const v = store.view;
    const friendly = pal.isFriendly(t.owner);
    // red only for warheads that hit something of ours; open ground gets a calm amber mark, an
    // ally's ground a calm teal one
    const threat = friendly ? undefined : store.inboundThreats.find((x) => x.id === t.id);
    // (a city braced through the impact has nothing left to lose: calm too)
    const danger = !!threat?.stake && threat.bracedSeconds === null;
    const calm = !!threat && !danger;
    const allyHit = !threat && !friendly && store.allyThreats.some((x) => x.id === t.id);
    const col: RGB = danger ? COLORS.danger : pal.color(t.owner);
    const head = missilePos(t, Math.min(tick, impact), { x: 0, y: 0 });
    const c = missileControlPoint(t.kind, t.ox, t.oy, t.tx, t.ty);
    const tauNow = Math.min(1, Math.max(0, (tick - t.launchTick) / t.flightTicks));
    const seenTick = friendly ? t.launchTick : (store.trackSeen.get(t.id) ?? v.tick);
    // our own missiles: a fading tail of the last part of the flight; foreign: what we saw
    const tauSeen = friendly ? Math.max(0, tauNow - 0.35) : Math.min(tauNow, Math.max(0, (seenTick - t.launchTick) / t.flightTicks));
    const at = (tau: number, o: Vec2) => bezierPoint(t.ox, t.oy, c.x, c.y, t.tx, t.ty, missileProgress(t.kind, tau), o);

    // trail: only the part our sensors have seen
    const a = { x: 0, y: 0 };
    const b = { x: 0, y: 0 };
    const trailA = friendly ? 0.6 : 1;
    for (let i = 0; i < TRAIL_STEPS; i++) {
      const t0 = tauSeen + ((tauNow - tauSeen) * i) / TRAIL_STEPS;
      const t1 = tauSeen + ((tauNow - tauSeen) * (i + 1)) / TRAIL_STEPS;
      at(t0, a);
      at(t1, b);
      const q0 = i / TRAIL_STEPS;
      const q1 = (i + 1) / TRAIL_STEPS;
      const f0 = friendly ? q0 * q0 : 0.2 + 0.8 * q0;
      const f1 = friendly ? q1 * q1 : 0.2 + 0.8 * q1;
      vb.line(a.x, a.y, b.x, b.y, col, trailA, { width: 0.8 + 2 * q1, fadeA: f0, fadeB: f1 });
    }
    if (!friendly) {
      // a few dots of back-plot into the fog: it came from out there
      for (let i = 1; i <= 7; i++) {
        at(Math.max(0, tauSeen - i * 0.012), a);
        vb.disc(a.x, a.y, 1, col, 0.42 * (1 - i / 8));
      }
    }
    // projected path to impact
    const steps = 24;
    for (let i = 0; i < steps; i++) {
      at(tauNow + ((1 - tauNow) * i) / steps, a);
      at(tauNow + ((1 - tauNow) * (i + 1)) / steps, b);
      vb.line(a.x, a.y, b.x, b.y, col, danger ? 0.9 : 0.3, { width: danger ? 1.5 : 1, dash: [3, 5], phase: i * 7 });
    }
    if (!friendly && tick <= impact) out.heads.set(t.id, { x: head.x, y: head.y });
    // head
    vb.glowDot(head.x, head.y, danger ? 22 : 14, col, danger ? 0.75 : 0.55);
    vb.disc(head.x, head.y, danger ? 2.6 : 2, COLORS.white, 1);
    // first seen: flash where it popped out of the fog
    const sinceSeen = (tick - seenTick) / TICK_HZ;
    if (!friendly && sinceSeen < 2) {
      at(tauSeen, a);
      vb.glowDot(a.x, a.y, 30, col, 0.45 * (1 - sinceSeen / 2));
    }
    if (danger) {
      vb.ring(t.tx, t.ty, 16, col, 0.85, { px: true, width: 1.3, dash: [3, 3] });
      vb.ring(t.tx, t.ty, 30, col, 0.3, { px: true, width: 1, dash: [2, 5] });
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        vb.glyph(t.tx, t.ty, dx * 19, dy * 19, dx * 24, dy * 24, col, 0.9, { width: 1.3 });
      }
    } else if (calm) {
      vb.ring(t.tx, t.ty, 12, COLORS.amber, 0.6, { px: true, width: 1.2, dash: [3, 3] });
    } else if (allyHit) {
      vb.ring(t.tx, t.ty, 12, COLORS.ally, 0.7, { px: true, width: 1.2, dash: [3, 3] });
    } else if (friendly) {
      vb.glyph(t.tx, t.ty, -3, -3, 3, 3, col, 0.5, { width: 1 });
      vb.glyph(t.tx, t.ty, -3, 3, 3, -3, col, 0.5, { width: 1 });
    }
  }

  /**
   * A shot at an aircraft — a SAM's or a carrier's interceptor (flies, homing on it) or a fighter's
   * missile (instant in the sim: drawn as a quick streak over `AA_SHOT_TICKS`) — from the shooter
   * we see (or remember), else from the direction it came (`ox, oy`: never the unseen shooter's
   * spot), then a burst where the aircraft was: amber fire on a hit, a small × on a miss.
   */
  private airShot(store: GameStore, out: SceneOut, i: InterceptEvent, tick: number): void {
    const v = store.view;
    const to = out.pos.get(i.targetId) ?? store.airAt(i.targetId);
    if (!to) return;
    const from = out.pos.get(i.shooterId) ?? v.ghosts.get(i.shooterId) ?? (i.ox !== undefined && i.oy !== undefined ? { x: i.ox, y: i.oy } : null);
    const instant = i.resolveTick <= i.fireTick;
    const end = instant ? i.fireTick + AA_SHOT_TICKS : i.resolveTick;
    if (from && tick >= i.fireTick && tick <= end) {
      const k = (tick - i.fireTick) / Math.max(1, end - i.fireTick);
      if (instant) this.aaStreak(from, to, k);
      else this.interceptor(from, to, k);
    }
    if (i.hit === undefined || tick < end) return;
    const age = (tick - end) / TICK_HZ;
    if (age > 2.5) return;
    const k = 1 - age / 2.5;
    const vb = this.vb;
    if (i.hit) {
      // the aircraft goes up in amber: a flash, a ring and four sparks thrown out
      vb.glowDot(to.x, to.y, 24, COLORS.amber, 0.9 * k);
      vb.disc(to.x, to.y, 2.2 * k, COLORS.white, k);
      vb.ring(to.x, to.y, 5 + 20 * (1 - k), COLORS.amber, 0.85 * k, { px: true, width: 1.5, glow: 4 });
      const r0 = 4 + 10 * (1 - k);
      for (let j = 0; j < 4; j++) {
        const a = j * (Math.PI / 2) + 0.6;
        vb.glyph(to.x, to.y, Math.cos(a) * r0, Math.sin(a) * r0, Math.cos(a) * (r0 + 4), Math.sin(a) * (r0 + 4), COLORS.amber, k, { width: 1.4 });
      }
    } else {
      const s = 5;
      vb.glyph(to.x, to.y, -s, -s, s, s, COLORS.neutral, 0.8 * k, { width: 1.4 });
      vb.glyph(to.x, to.y, -s, s, s, -s, COLORS.neutral, 0.8 * k, { width: 1.4 });
    }
  }

  /** A fighter's air-to-air missile: a bright dash racing from the shooter to the target, a muzzle flash. */
  private aaStreak(from: Vec2, to: Vec2, k: number): void {
    const vb = this.vb;
    const at = (u: number) => ({ x: from.x + (to.x - from.x) * u, y: from.y + (to.y - from.y) * u });
    const head = at(k);
    const tail = at(Math.max(0, k - 0.35));
    vb.line(tail.x, tail.y, head.x, head.y, COLORS.white, 0.95, { width: 2, glow: 4 });
    vb.line(from.x, from.y, to.x, to.y, COLORS.white, 0.18, { width: 0.8, dash: [1, 4] });
    vb.glowDot(head.x, head.y, 10, COLORS.white, 0.9);
    if (k < 0.4) vb.glowDot(from.x, from.y, 12, COLORS.amber, 0.8 * (1 - k / 0.4));
  }

  private interceptor(from: Vec2, to: Vec2, k: number): void {
    const vb = this.vb;
    const d = Math.hypot(to.x - from.x, to.y - from.y);
    const cx = (from.x + to.x) / 2;
    const cy = (from.y + to.y) / 2 - d * 0.45;
    const p0 = this.tmp;
    let px = from.x;
    let py = from.y;
    const n = 16;
    for (let i = 1; i <= n; i++) {
      const u = (k * i) / n;
      bezierPoint(from.x, from.y, cx, cy, to.x, to.y, u, p0);
      const q = i / n;
      vb.line(px, py, p0.x, p0.y, COLORS.white, 0.2 + 0.8 * q, { width: 0.8 + 1.6 * q });
      px = p0.x;
      py = p0.y;
    }
    for (let i = 0; i < 10; i++) {
      const u0 = k + ((1 - k) * i) / 10;
      const u1 = k + ((1 - k) * (i + 1)) / 10;
      const a = bezierPoint(from.x, from.y, cx, cy, to.x, to.y, u0);
      const b = bezierPoint(from.x, from.y, cx, cy, to.x, to.y, u1);
      vb.line(a.x, a.y, b.x, b.y, COLORS.me, 0.45, { width: 1, dash: [1, 4] });
    }
    vb.glowDot(px, py, 16, COLORS.me, 0.85);
    vb.disc(px, py, 1.6, COLORS.white, 1);
    vb.glowDot(from.x, from.y, 10, COLORS.me, 0.5 * (1 - k));
    const s = 6;
    for (const [ax, ay, bx, by] of [
      [0, -s, s, 0],
      [s, 0, 0, s],
      [0, s, -s, 0],
      [-s, 0, 0, -s],
    ]) {
      vb.glyph(to.x, to.y, ax, ay, bx, by, COLORS.white, 0.9, { width: 1.2 });
    }
  }

  /**
   * Aim: flight paths from the launchers, a reticle coloured by what is under it (own land
   * red, an ally's land red and blocked ⊘, enemy amber, unseen dim) and the ring 0/1/2 blast
   * footprint. Captions (flight time, OWN TERRITORY …) are labels.
   */
  private aim(aim: NonNullable<Overlay['aim']>, st: GameStore['aim'], time: number): void {
    const vb = this.vb;
    let col: RGB = aim.valid ? COLORS.me : COLORS.danger;
    if (st) {
      if (st.zone === 'own' || st.zone === 'ally' || st.blocked) col = COLORS.danger;
      else if (st.reason) col = COLORS.neutral;
      else if (st.fog) col = COLORS.neutral;
      else if (st.zone === 'water') col = COLORS.me;
      else col = COLORS.amber;
    }
    const pathA = st && (st.blocked || st.reason) ? 0.35 : 0.7;
    // an SLBM reaches 70 mu: each aiming sub shows its reach (dim once the cursor is beyond it)
    if (aim.kind === 'slbm') {
      for (const f of aim.from) {
        const inside = Math.hypot(aim.x - f.x, aim.y - f.y) <= BALANCE.sub.slbmRange;
        this.vb.ring(f.x, f.y, BALANCE.sub.slbmRange, inside ? COLORS.me : COLORS.neutral, inside ? 0.6 : 0.4, { width: 1.4, dash: [6, 4] });
      }
    }
    for (const f of aim.from) {
      if (aim.kind === 'icbm' || aim.kind === 'slbm') {
        const c = missileControlPoint(aim.kind, f.x, f.y, aim.x, aim.y);
        const n = 32;
        const a = { x: 0, y: 0 };
        const b = { x: 0, y: 0 };
        for (let i = 0; i < n; i++) {
          bezierPoint(f.x, f.y, c.x, c.y, aim.x, aim.y, i / n, a);
          bezierPoint(f.x, f.y, c.x, c.y, aim.x, aim.y, (i + 1) / n, b);
          vb.line(a.x, a.y, b.x, b.y, col, pathA, { width: 1.2, dash: [3, 4], phase: i * 7 });
        }
      } else {
        vb.line(f.x, f.y, aim.x, aim.y, col, 0.6, { width: 1.2, dash: [3, 4] });
      }
    }
    const x = st?.x ?? aim.x;
    const y = st?.y ?? aim.y;
    const armed = st?.confirmArmed ? 0.6 + 0.4 * Math.sin(time * 12) : 1;
    vb.ring(x, y, 12, col, 0.9 * armed, { px: true, width: 1.3 });
    if (st?.blocked || st?.zone === 'ally') {
      // ⊘: blocked
      vb.glyph(x, y, -8, 8, 8, -8, col, 0.9, { width: 1.5 });
    } else {
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        vb.glyph(x, y, dx * 6, dy * 6, dx * 16, dy * 16, col, 0.9, { width: 1.2 });
      }
    }
    if (st) {
      const [r0, r1, r2] = st.blastRingsMu;
      vb.ring(x, y, r0, col, 0.45, { width: 1 });
      vb.ring(x, y, r1, col, 0.3, { width: 1, dash: [3, 3] });
      vb.ring(x, y, r2, col, 0.2, { width: 1, dash: [2, 5] });
    } else if (aim.kind === 'icbm' || aim.kind === 'slbm') {
      vb.ring(x, y, blastRadius(this.hexR), col, 0.25, { width: 1, dash: [2, 4] });
    }
  }

}
